import { useMemo } from "react";
import { motion } from "framer-motion";
import { revealLeft, Eyebrow, OutlineButton, TextCta, VIEWPORT, EASE_LUXE } from "./primitives";
import PassportPreview from "./PassportPreview";
import { buildDemoPassport } from "@/lib/demoPassportPreview";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * Shows, rather than tells, what a passport holds, where RegistrySection
 * lists the same thing in words.
 *
 * The section owns the demo dossier; PassportPreview stays data-driven, so
 * swapping in a real dossier later is a one-line change here.
 */
export const PassportPreviewSection = ({ variant = "landing", registerTo }) => {
  const { t } = useLanguage();
  const passport = useMemo(() => buildDemoPassport(t), [t]);
  const isPage = variant === "page";

  return (
    <section
      id="passeport-apercu"
      data-testid="passport-preview-section"
      className={`relative scroll-mt-28 overflow-hidden ${
        isPage ? "py-10 md:py-16" : "py-14 md:py-32 border-t border-ink/5"
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-5 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT}
          variants={revealLeft}
        >
          <Eyebrow testId="passport-preview-eyebrow">
            {t("passportPreview.eyebrow")}
          </Eyebrow>
          {isPage ? (
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight mb-6">
              {t("passportPreview.title")}
            </h1>
          ) : (
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight mb-6">
              {t("passportPreview.title")}
            </h2>
          )}
          <p className="text-base md:text-lg text-ink-muted leading-relaxed max-w-xl">
            {t("passportPreview.subtitle")}
          </p>

          {/* Sur /passeport la page porte déjà ses propres appels en pied ;
              sur l'accueil, la maquette doit pouvoir mener au dossier réel. */}
          {!isPage && (
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <OutlineButton
                to="/passeport/demo"
                track="passport-preview"
                testId="passport-preview-demo"
              >
                {t("passportPreview.demoCta")}
              </OutlineButton>
              <TextCta
                to={registerTo}
                track="passport-preview"
                testId="passport-preview-register"
              >
                {t("finalCta.register")}
              </TextCta>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.8, ease: EASE_LUXE }}
          className="flex justify-center lg:justify-end"
        >
          <PassportPreview passport={passport} />
        </motion.div>
      </div>
    </section>
  );
};

export default PassportPreviewSection;
